// TanStack Query hooks for data_sources CRUD.
// All atomic writes go through Supabase direct inserts (single-row, no transaction needed).
// Storage upload is a separate call after the DB row is created.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { DataSource, DataSourceSchema } from "@/types/ontology";

// ── Query keys (plan §1.3) ────────────────────────────────────────────────────

export const DATA_SOURCES_KEY = ["data-sources"] as const;

// ── Query ─────────────────────────────────────────────────────────────────────

export function useDataSources() {
  const query = useQuery({
    queryKey: DATA_SOURCES_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("data_sources")
        .select("*")
        .is("deleted_at", null)
        .order("uploaded_at", { ascending: false });

      if (error) throw new Error(error.message);

      return (data as DataSource[]) ?? [];
    },
  });

  return { dataSources: query.data ?? [], isLoading: query.isLoading, error: query.error };
}

// ── Uploader name resolution ────────────────────────────────────────────────
// Resolves a set of uploader user ids to display names via the `profiles` table
// (joined on profiles.user_id). Falls back to email, then "Unknown". Mirrors the
// name-resolution pattern used in PARClientPanel / AgentFileEditor.

export function useUploaderNames(userIds: (string | null)[]) {
  const ids = Array.from(new Set(userIds.filter((id): id is string => !!id)));

  const query = useQuery({
    queryKey: ["uploader-names", ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, display_name, email")
        .in("user_id" as never, ids);

      if (error) throw new Error(error.message);

      const map: Record<string, string> = {};
      for (const row of (data ?? []) as Array<{
        user_id: string;
        display_name: string | null;
        email: string | null;
      }>) {
        map[row.user_id] = row.display_name || row.email || "Unknown";
      }
      return map;
    },
  });

  const namesById = query.data ?? {};

  const resolve = (userId: string | null): string => {
    if (!userId) return "Unknown";
    return namesById[userId] ?? "Unknown";
  };

  return { namesById, resolve, isLoading: query.isLoading };
}

// ── Mutations ─────────────────────────────────────────────────────────────────

interface UploadCsvArgs {
  file: File;
  name: string;
  schema: DataSourceSchema;
  rowCount: number;
}

/**
 * Upload a CSV data source: insert the DB row, then upload the file to storage.
 * Returns the created DataSource row.
 */
export function useUploadCsv() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ file, name, schema, rowCount }: UploadCsvArgs) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("not authenticated");

      const timestamp = Date.now();
      const storagePath = `${user.id}/${timestamp}_${file.name}`;

      // Insert the DB row first (no storage_path yet — updated after upload).
      const { data: row, error: insertError } = await supabase
        .from("data_sources")
        .insert({
          kind: "csv",
          name,
          schema: schema as unknown as Record<string, unknown>,
          row_count: rowCount,
          uploaded_by: user.id,
        })
        .select()
        .single();

      if (insertError) throw new Error(insertError.message);

      // Upload the file to the data-sources bucket.
      const { error: uploadError } = await supabase.storage
        .from("data-sources")
        .upload(storagePath, file, { contentType: "text/csv" });

      if (uploadError) {
        // Best-effort cleanup: soft-delete the row so it doesn't orphan.
        console.error("hook:useUploadCsv storage upload failed, soft-deleting row", uploadError);
        await supabase
          .from("data_sources")
          .update({ deleted_at: new Date().toISOString() })
          .eq("id", row.id);
        throw new Error(uploadError.message);
      }

      // Update storage_path on the row.
      const { data: updated, error: updateError } = await supabase
        .from("data_sources")
        .update({ storage_path: storagePath })
        .eq("id", row.id)
        .select()
        .single();

      if (updateError) throw new Error(updateError.message);

      return updated as DataSource;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DATA_SOURCES_KEY });
    },
    onError: (err) => {
      console.error("hook:useUploadCsv", err);
      toast({
        title: "Failed to upload data source",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });
}

export function useSoftDeleteDataSource() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("data_sources")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id)
        .is("deleted_at", null);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DATA_SOURCES_KEY });
    },
    onError: (err) => {
      console.error("hook:useSoftDeleteDataSource", err);
      toast({
        title: "Failed to delete data source",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });
}
